import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Sparkles, Image, FileText, Layers, Check, ArrowRight, 
  Clock, Palette, MessageSquare, Star, Zap
} from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';

const PostaJaDemo = () => {
  const navigate = useNavigate();

  const planos = [
    {
      nome: 'Básico',
      preco: 49.99,
      popular: false,
      conteudos: [
        '1 Story por dia (30/mês)',
        '2 Posts por semana (8/mês)',
        'Total: 38 conteúdos/mês'
      ],
      recursos: [
        'Imagens personalizadas',
        'Textos com IA',
        'Suas cores e estilo',
        'Dashboard exclusivo',
        'Notificação WhatsApp'
      ]
    },
    {
      nome: 'Plus',
      preco: 79.99,
      popular: true,
      conteudos: [
        'Tudo do Básico',
        '+ 2 Carrosséis/mês (10 imagens)',
        'Total: 48 conteúdos/mês'
      ],
      recursos: [
        'Tudo do Básico',
        'Carrosséis automáticos',
        'Suporte prioritário',
        'Conteúdo premium'
      ]
    }
  ];

  const comoFunciona = [
    { icon: FileText, titulo: 'Conte sobre seu negócio', desc: 'Preencha um formulário simples com seu nicho, público e cores' },
    { icon: Sparkles, titulo: 'IA cria seu conteúdo', desc: 'Todo dia às 22h, geramos seus posts com imagens e textos' },
    { icon: MessageSquare, titulo: 'Receba no WhatsApp', desc: 'Notificamos quando está pronto para você aprovar' },
    { icon: Image, titulo: 'Baixe e publique', desc: 'Conteúdo pronto para postar nas suas redes' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero */}
      <section className="pt-24 pb-16 px-4 bg-gradient-to-br from-violet-50 via-white to-fuchsia-50">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-violet-100 rounded-full text-violet-700 text-sm font-medium mb-6">
            <Sparkles size={16} />
            Conteúdo Gerado por IA
          </div>
          
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
            Seus posts{' '}
            <span className="text-violet-600">prontos</span>,
            <br />todo dia,{' '}
            <span className="text-violet-600">no automático</span>
          </h1>
          
          <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto">
            A IA cria stories, posts e carrosséis personalizados para seu negócio. 
            Você só baixa e publica. Simples assim.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Button 
              onClick={() => navigate('/postaja/configurar')}
              className="bg-violet-600 hover:bg-violet-700 text-lg px-8 py-6"
              data-testid="btn-comecar"
            >
              Começar agora
              <ArrowRight size={20} />
            </Button>
          </div>

          <div className="flex items-center justify-center gap-8 text-sm text-slate-500">
            <span className="flex items-center gap-2">
              <Check size={16} className="text-violet-500" />
              38+ conteúdos/mês
            </span>
            <span className="flex items-center gap-2">
              <Check size={16} className="text-violet-500" />
              Suas cores e estilo
            </span>
            <span className="flex items-center gap-2">
              <Check size={16} className="text-violet-500" />
              Pronto para postar
            </span>
          </div>
        </div>
      </section>

      {/* Como Funciona */}
      <section className="py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-12">
            Como funciona
          </h2>
          
          <div className="grid md:grid-cols-4 gap-6">
            {comoFunciona.map((item, i) => (
              <div key={i} className="text-center">
                <div className="w-14 h-14 rounded-2xl bg-violet-100 flex items-center justify-center mx-auto mb-4">
                  <item.icon className="text-violet-600" size={28} />
                </div>
                <div className="text-violet-600 text-sm font-bold mb-2">Passo {i + 1}</div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{item.titulo}</h3>
                <p className="text-slate-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* O que você recebe */}
      <section className="py-16 px-4 bg-slate-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-4">
            O que você recebe todo mês
          </h2>
          <p className="text-slate-600 text-center mb-12">
            Conteúdo profissional, personalizado com suas cores e estilo
          </p>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-6 border border-slate-200 hover:border-violet-300 hover:shadow-lg transition-all">
              <div className="w-12 h-12 rounded-xl bg-pink-100 flex items-center justify-center mb-4">
                <Image className="text-pink-600" size={24} />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2">Stories</h3>
              <p className="text-slate-600 mb-4">1 por dia • 30/mês</p>
              <p className="text-sm text-slate-500">Imagens verticais prontas para Stories do Instagram e Status do WhatsApp</p>
            </div>
            
            <div className="bg-white rounded-2xl p-6 border border-slate-200 hover:border-violet-300 hover:shadow-lg transition-all">
              <div className="w-12 h-12 rounded-xl bg-violet-100 flex items-center justify-center mb-4">
                <FileText className="text-violet-600" size={24} />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2">Posts</h3>
              <p className="text-slate-600 mb-4">2 por semana • 8/mês</p>
              <p className="text-sm text-slate-500">Imagens quadradas com legendas, hashtags e call-to-action</p>
            </div>
            
            <div className="bg-white rounded-2xl p-6 border border-slate-200 hover:border-violet-300 hover:shadow-lg transition-all">
              <div className="w-12 h-12 rounded-xl bg-fuchsia-100 flex items-center justify-center mb-4">
                <Layers className="text-fuchsia-600" size={24} />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2">Carrosséis</h3>
              <p className="text-slate-600 mb-4">2/mês • 10 imagens</p>
              <p className="text-sm text-slate-500">5 slides por carrossel, perfeitos para educar e engajar (Plano Plus)</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-4">
            Escolha seu plano
          </h2>
          <p className="text-slate-600 text-center mb-12">
            Cancele quando quiser. Sem fidelidade.
          </p>
          
          <div className="grid md:grid-cols-2 gap-8">
            {planos.map((plano, i) => (
              <div 
                key={i} 
                className={`rounded-2xl p-8 border-2 ${plano.popular ? 'border-violet-500 bg-violet-50' : 'border-slate-200 bg-white'} relative`}
              >
                {plano.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-violet-500 text-white text-xs font-bold px-4 py-1 rounded-full">
                    MAIS POPULAR
                  </div>
                )}
                
                <h3 className="text-2xl font-bold text-slate-900 mb-2">{plano.nome}</h3>
                <div className="text-4xl font-bold text-slate-900 mb-6">
                  R$ {plano.preco.toFixed(2).replace('.', ',')}<span className="text-lg text-slate-400">/mês</span>
                </div>
                
                <div className="mb-6">
                  <p className="text-sm font-medium text-slate-700 mb-2">Conteúdos:</p>
                  <ul className="space-y-2">
                    {plano.conteudos.map((c, j) => (
                      <li key={j} className="flex items-center gap-2 text-slate-600">
                        <Check size={16} className="text-violet-500" />
                        {c}
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="mb-8">
                  <p className="text-sm font-medium text-slate-700 mb-2">Incluso:</p>
                  <ul className="space-y-2">
                    {plano.recursos.map((r, j) => (
                      <li key={j} className="flex items-center gap-2 text-slate-600 text-sm">
                        <Check size={14} className="text-green-500" />
                        {r}
                      </li>
                    ))}
                  </ul>
                </div>
                
                <Button 
                  onClick={() => navigate(`/postaja/configurar?plano=${plano.nome.toLowerCase()}`)}
                  className={`w-full ${plano.popular ? 'bg-violet-600 hover:bg-violet-700' : 'bg-slate-800 hover:bg-slate-900'}`}
                  data-testid={`btn-plano-${plano.nome.toLowerCase()}`}
                >
                  Escolher {plano.nome}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 bg-gradient-to-r from-violet-600 to-fuchsia-600">
        <div className="max-w-2xl mx-auto text-center text-white">
          <Zap size={48} className="mx-auto mb-6" />
          <h2 className="text-3xl font-bold mb-4">
            Pare de pensar no que postar
          </h2>
          <p className="text-violet-100 mb-8">
            A IA faz isso por você. Comece agora e receba seu primeiro conteúdo hoje.
          </p>
          <Button 
            onClick={() => navigate('/postaja/configurar')}
            size="lg"
            className="bg-white text-violet-600 hover:bg-violet-50 text-lg px-8"
          >
            Começar agora
            <ArrowRight size={20} />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PostaJaDemo;
